import { Box, Fade, Paper, Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import { CartItemChangeQuantityRequest, CartItemRequest, EventPhoto, PhotoEvent, PhotoEventWithPricelistAndPhotos } from 'common-models';
import { FC, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useAuth } from "react-oidc-context";
import { useChangeProductQuantityMutation } from '../../features/events/eventShopApiSlice';
import { useGetSubEventsQuery, useLazyGetPhotoDownloadUrlQuery } from '../../features/events/eventsApiSlice';
import { Model } from '../../lib/Model';
import { DownloadFunctions } from './DownloadFunctions';
import { EventGalleryPhoto } from './EventGalleryPhoto';
import { EventsBreadCrumb } from './EventsBreadCrumb';
import EventsList from './EventsList';
import ImagesGalleryView from './ImagesGalleryView';
import { PricelistView } from './PricelistView';
import { DownloadButtonComponent } from './components/DownloadButtonComponent';
import { useDownloadManager } from './hooks/useDownloadManager';
import { useStickyHeader } from './hooks/useStickyHeader';
import { getEventDateString, setWindowLocation, ViewStyle } from './utils/eventUtils';

type EventPageContentProps = {
  eventWithPricelistAndPhotos: PhotoEventWithPricelistAndPhotos,
  selectedImageId: string,
}
const EventPageContent: FC<EventPageContentProps> = ({ eventWithPricelistAndPhotos, selectedImageId }) => {
  const auth = useAuth();
  const { data: subEventsFromApi = undefined, isLoading: subEventsFromApiIsLoading } = useGetSubEventsQuery({ eventId: eventWithPricelistAndPhotos.event.id });
  const [getDownloadUrl] = useLazyGetPhotoDownloadUrlQuery({});
  const [changeProductQuantity] = useChangeProductQuantityMutation();

  // Custom hooks
  const [downloadState, downloadActions] = useDownloadManager(eventWithPricelistAndPhotos.photos);
  const { showStickyDownloadButton, headerHeight, downloadSectionRef } = useStickyHeader();

  // Component state
  const [currentViewStyle, setCurrentViewStyle] = useState(ViewStyle.Grid);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(-1);
  const [subEvents, setSubEvents] = useState<PhotoEvent[]>([]);
  const [topScrollValue, setTopScrollValue] = useState(0);
  const [previousEventId, setPreviousEventId] = useState('');
  const [isMobileDevice, setIsMobileDevice] = useState(isMobile);
  const pageRootUrl = `/events/${eventWithPricelistAndPhotos.event.id}`;


  // Update mobile device status
  useEffect(() => {
    setIsMobileDevice(isMobile);
  }, []);

  useEffect(() => {
    if (subEventsFromApiIsLoading) {
      setSubEvents([]);
    }
    else if (subEventsFromApi) {
      setSubEvents(subEventsFromApi.map(x => Model.deepCopyEvent(x)).sort((a, b) => {
        let result = a.eventStartTime.valueOf() - b.eventStartTime.valueOf();
        if (result === 0) {
          result = a.title < b.title ? -1 : 1;
        }
        return result;
      }));
    }
  }, [subEventsFromApi, subEventsFromApiIsLoading])

  useEffect(() => {
    if (previousEventId !== eventWithPricelistAndPhotos.event.id) {
      setCurrentPhotoIndex(-1);
      setPreviousEventId(eventWithPricelistAndPhotos.event.id)
    }
  }, [eventWithPricelistAndPhotos, previousEventId])

  useEffect(() => {
    if (currentViewStyle === ViewStyle.Grid) {
      setWindowLocation(pageRootUrl);
      window.scrollTo({
        top: topScrollValue
      })
    }
  }, [currentViewStyle, pageRootUrl, topScrollValue])



  useEffect(() => {

    if (currentPhotoIndex >= 0) {
      const eventPhoto = eventWithPricelistAndPhotos.photos[currentPhotoIndex];
      if (eventPhoto !== undefined) {
        setWindowLocation(`${pageRootUrl}/${eventPhoto.id}`);

        setTopScrollValue(window.scrollY);
        setCurrentViewStyle(ViewStyle.Gallery);
        return;
      }
    }
    setCurrentViewStyle(ViewStyle.Grid);

    setWindowLocation(pageRootUrl);

  }, [currentPhotoIndex, eventWithPricelistAndPhotos.photos, pageRootUrl])

  useEffect(() => {
    if (selectedImageId !== undefined) {
      const index = eventWithPricelistAndPhotos.photos.findIndex(
        (x) => x.id === selectedImageId
      );
      setCurrentPhotoIndex(index)
    }
  }, [eventWithPricelistAndPhotos.photos, selectedImageId]);

  const handleImageSelected = (eventPhoto: EventPhoto) => {
    const index = eventWithPricelistAndPhotos.photos.findIndex(
      (x) => x.id === eventPhoto.id
    );
    if (index >= 0) {
      setCurrentPhotoIndex(index);
    }
  };

  const downloadImage = async (eventId: string, imageId: string) => {
    const response = await getDownloadUrl({
      eventId: eventId,
      imageId: imageId,
    });

    if (response.isSuccess) {
      const url = response.data;
      DownloadFunctions.triggerDownload(document, url, imageId);
    }
  }

  const addToShoppingCart = async (photo: EventPhoto, productId: string | undefined) => {
    if (!productId) {
      console.error("Product ID is required to add to shopping cart.");
      return;
    }

    const request: CartItemRequest<CartItemChangeQuantityRequest> = {
      type: "change-quantity",
      request: {
        delta: 1,
        eventId: photo.eventId,
        imageId: photo.id,
        productId: productId
      }
    }
    // await changeProductQuantity({ visitorId: userState.visitorId, request });
  }

  const onLargeViewImageChanged = (index: number) => {
    setCurrentPhotoIndex(index);
  }

  return (
    <>
      {/* Sticky download button */}
      <Fade in={showStickyDownloadButton && downloadState.selectedImages.length > 0}>
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            top: `${headerHeight}px`,
            left: 0,
            right: 0,
            zIndex: 1200,
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
            <DownloadButtonComponent
              isSticky={true}
              downloadState={downloadState}
              downloadActions={downloadActions}
              eventPhotos={eventWithPricelistAndPhotos.photos}
            />
          </Box>
        </Paper>
      </Fade>

      {currentViewStyle === ViewStyle.Grid ? (
        <>
          <EventsBreadCrumb event={eventWithPricelistAndPhotos.event} />
          <Typography align="center" component={"h1"} variant="h1">
            {" "}
            {eventWithPricelistAndPhotos.event.title}
          </Typography>
          <Typography align="center">
            {getEventDateString(eventWithPricelistAndPhotos)}
          </Typography>

          <EventsList events={subEvents} />

          {
            eventWithPricelistAndPhotos.event.isFreeOfCharge && eventWithPricelistAndPhotos.photos && eventWithPricelistAndPhotos.photos.length > 0
              ? <Box ref={downloadSectionRef}>
                <DownloadButtonComponent
                  downloadState={downloadState}
                  downloadActions={downloadActions}
                  eventPhotos={eventWithPricelistAndPhotos.photos}
                />
              </Box>
              : <></>
          }

          <PricelistView
            pricelist={eventWithPricelistAndPhotos.pricelist}
            isFreeOfCharge={eventWithPricelistAndPhotos.event.isFreeOfCharge}
            displayFreeOfChargeText={eventWithPricelistAndPhotos.event.displayFreeOfChargeText}
            hasPhotos={eventWithPricelistAndPhotos.photos && eventWithPricelistAndPhotos.photos.length > 0} />

          <Box sx={{ paddingTop: showStickyDownloadButton && downloadState.selectedImages.length > 0 ? `${headerHeight + 80}px` : 0 }}>
            <Grid container>
              {eventWithPricelistAndPhotos.photos.map((eventPhoto) => (
                <EventGalleryPhoto
                  key={eventPhoto.id}
                  eventPhoto={eventPhoto}
                  isFreeOfCharge={eventWithPricelistAndPhotos.event.isFreeOfCharge}
                  loadLazy={true}
                  isSelectedForDownload={DownloadFunctions.imageIsSelectedForDownload(downloadState.selectedImages, eventPhoto.eventId, eventPhoto.id)}
                  onSelected={() => handleImageSelected(eventPhoto)}
                  onAddToShoppingCart={() => addToShoppingCart(eventPhoto, eventWithPricelistAndPhotos.pricelist?.products[0].productId)}
                  downloadImage={() => downloadImage(eventPhoto.eventId, eventPhoto.id)}
                  selectImageForDownload={() => downloadActions.toggleImageForDownload(eventPhoto.eventId, eventPhoto.id)}
                />
              ))}
            </Grid>
          </Box>
        </>
      ) : (
        <ImagesGalleryView
          event={eventWithPricelistAndPhotos}
          startIndex={currentPhotoIndex}
          onAddToShoppingCart={
            () => addToShoppingCart(
              eventWithPricelistAndPhotos.photos[currentPhotoIndex],
              eventWithPricelistAndPhotos.pricelist?.products[0].productId)
          }
          downloadImage={
            () => downloadImage(
              eventWithPricelistAndPhotos.photos[currentPhotoIndex].eventId,
              eventWithPricelistAndPhotos.photos[currentPhotoIndex].id)
          }
          onImageChanged={onLargeViewImageChanged}
          onCloseGalleryView={() => setCurrentViewStyle(ViewStyle.Grid)} />
      )}
    </>
  )
}

export default EventPageContent;
